import { useState } from "react";
import { Bell, Send, Users } from "lucide-react";
import { Table, Button, Form, Input, Select, Tag, message, Spin } from "antd";
import type { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { INotification, IWorkerStat } from "../../../types/api/payslips";
import {
  useNotificationList,
  useNotificationRecipients,
  useNotificationDispatch,
  useWorkerStats,
} from "../../../hooks/api/payslips/hooks";
import { Tabs } from "antd";

const PRIORITY_COLOR: Record<string, string> = { low: "default", normal: "blue", high: "orange", urgent: "red" };
const CHANNEL_COLOR: Record<string, string> = { in_app: "cyan", email: "blue", sms: "green", push: "purple" };

function DispatchTab() {
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const { data: recipRes, isLoading: loadingRecip } = useNotificationRecipients();
  const recipients: any[] = (recipRes?.data as any) ?? [];

  const dispatch = useNotificationDispatch({
    onSuccess: () => { msgApi.success("Notification dispatched."); form.resetFields(); },
    onError: (e) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  return (
    <div className="max-w-lg">
      {ctx}
      <p className="text-sm text-slate-500 mb-4">Send an in-app notification to selected workers.</p>
      <Form form={form} layout="vertical" size="middle" onFinish={(vals) => dispatch.mutate(vals)}>
        <Form.Item name="user_ids" label="Recipients" rules={[{ required: true, message: "Select at least one worker" }]}>
          <Select
            mode="multiple"
            placeholder="Select workers…"
            loading={loadingRecip}
            showSearch
            filterOption={(v, o) => (o?.label as string)?.toLowerCase().includes(v.toLowerCase())}
            options={[
              { value: "__all", label: "All Workers" },
              ...recipients.map((u: any) => ({
                value: u.id,
                label: [u.name, u.job_title].filter(Boolean).join(" — "),
              })),
            ]}
            onChange={(vals) => {
              if (vals.includes("__all")) {
                form.setFieldValue("user_ids", recipients.map((u: any) => u.id));
              }
            }}
          />
        </Form.Item>
        <Form.Item name="title" label="Title" rules={[{ required: true }]}>
          <Input placeholder="Notification title" />
        </Form.Item>
        <Form.Item name="body" label="Message">
          <Input.TextArea rows={3} placeholder="Optional notification body" />
        </Form.Item>
        <Button type="primary" htmlType="submit" icon={<Send size={14} />} loading={dispatch.isPending}>
          Dispatch Notification
        </Button>
      </Form>
    </div>
  );
}

function LogTab() {
  const { data: res, isLoading } = useNotificationList({ page: 1, limit: 50 });
  const notifications: INotification[] = (res?.data as any) ?? [];

  const columns: ColumnType<INotification>[] = [
    {
      title: "Sent",
      dataIndex: "created_at",
      render: (v) => <span className="text-xs text-slate-400">{dayjs(v).format("DD MMM HH:mm")}</span>,
      width: 120,
    },
    {
      title: "Recipient",
      dataIndex: "recipient_name",
      render: (v) => <span className="text-sm text-slate-700">{v || "—"}</span>,
      width: 160,
    },
    {
      title: "Title",
      dataIndex: "title",
      render: (v) => <span className="text-sm font-medium text-slate-800">{v}</span>,
    },
    {
      title: "Channel",
      dataIndex: "channel",
      render: (v) => <Tag color={CHANNEL_COLOR[v]}>{v.replace("_", " ")}</Tag>,
      width: 90,
    },
    {
      title: "Priority",
      dataIndex: "priority",
      render: (v) => <Tag color={PRIORITY_COLOR[v]}>{v}</Tag>,
      width: 80,
    },
    {
      title: "Read",
      dataIndex: "is_read",
      render: (v) => v ? <Tag color="green">Read</Tag> : <Tag color="default">Unread</Tag>,
      width: 75,
    },
  ];

  return (
    <Table
      dataSource={notifications}
      columns={columns}
      rowKey="id"
      loading={isLoading}
      pagination={{ pageSize: 50, showSizeChanger: false }}
      scroll={{ x: 700 }}
    />
  );
}

function StatsTab() {
  const { data: res, isLoading } = useWorkerStats({ page: 1, limit: 50 });
  const stats: IWorkerStat[] = (res?.data as any) ?? [];

  const columns: ColumnType<IWorkerStat>[] = [
    {
      title: "Worker",
      dataIndex: "name",
      render: (v, r) => (
        <div>
          <div className="font-medium text-slate-800 text-sm">{v || "—"}</div>
          {r.job_title && <div className="text-xs text-slate-400">{r.job_title}</div>}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (v) => <Tag color={v === "active" ? "green" : "default"}>{v}</Tag>,
      width: 90,
    },
    {
      title: "Total Jobs",
      dataIndex: "total_jobs",
      render: (v) => <span className="font-semibold text-slate-800">{v}</span>,
      width: 100,
      sorter: (a, b) => Number(a.total_jobs) - Number(b.total_jobs),
    },
    {
      title: "Completed",
      dataIndex: "completed_jobs",
      render: (v) => <span className="font-semibold text-emerald-600">{v}</span>,
      width: 100,
      sorter: (a, b) => Number(a.completed_jobs) - Number(b.completed_jobs),
      defaultSortOrder: "descend",
    },
    {
      title: "Completion Rate",
      key: "rate",
      render: (_, r) => {
        const rate = r.total_jobs > 0 ? Math.round((r.completed_jobs / r.total_jobs) * 100) : 0;
        return (
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-slate-100 rounded-full h-2 w-20">
              <div className="h-2 rounded-full" style={{ width: `${rate}%`, background: "var(--color-primary)" }} />
            </div>
            <span className="text-xs text-slate-600">{rate}%</span>
          </div>
        );
      },
      width: 140,
    },
  ];

  return (
    <Table
      dataSource={stats}
      columns={columns}
      rowKey="id"
      loading={isLoading}
      pagination={{ pageSize: 50, showSizeChanger: false }}
      scroll={{ x: 600 }}
    />
  );
}

const NotificationsPage = () => (
  <div className="p-6 min-h-screen">
    <div className="mb-6">
      <h1 className="text-xl font-semibold text-slate-800">Notifications & Worker Stats</h1>
      <p className="text-slate-500 text-sm">Dispatch notifications to workers · View notification log · Worker performance</p>
    </div>
    <Tabs
      items={[
        { key: "dispatch", label: <span className="flex items-center gap-1.5"><Send size={13} />Dispatch</span>, children: <DispatchTab /> },
        { key: "log", label: <span className="flex items-center gap-1.5"><Bell size={13} />Notification Log</span>, children: <LogTab /> },
        { key: "stats", label: <span className="flex items-center gap-1.5"><Users size={13} />Worker Stats</span>, children: <StatsTab /> },
      ]}
    />
  </div>
);

export default NotificationsPage;
